'use client';

import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { LotteryHeaderContainer } from '../../../LotteryHeaderContainer/LotteryHeaderContainer';
import { Select } from '../../../form-controls/Select/Select';
import { LotteryLayout } from '../../../lotteries/LotteryLayout/LotteryLayout';
import { getLotteryDrawFrequency } from '../../../lotteries/utils';
import { useOptionalLiveDraw } from '../../liveDraw/LiveDrawProvider';
import type { LotteryDrawResult } from '../../types';
import { LiveDrawPanel } from '../LiveDrawPanel/LiveDrawPanel';
import { LotteryResult } from '../LotteryResult/LotteryResult';
import { ResultsStatusOrNumbers } from '../ResultsStatusOrNumbers/ResultsStatusOrNumbers';

type ResultsHeaderProps = ComponentProps<typeof LotteryHeaderContainer> & {
  drawingDates: { value: string; text: string }[];
  currentResult: LotteryDrawResult & { drawingDate: string; drawingId: string };
  onDateChange: (value: string) => void;
  enableLiveDrawView?: boolean;
  isLiveDrawView?: boolean;
};

/**
 * ResultsHeader component displays the header section for the lottery results page.
 *
 * It provides a dropdown to select drawing dates, displays the selected result's numbers
 * or draw status when numbers are not yet available, and includes a link to view the archive. The component is styled according to the lottery name.
 *
 * @param {ResultsHeaderProps} props - The props for the ResultsHeader component.
 * @param {string} props.lotteryName - The name of the lottery.
 * @param {{ value: string; text: string }[]} props.drawingDates - The available drawing dates for selection.
 * @param {{ drawingId: string; drawingDate: string; numbers: number[]; extraNumbers?: number[] }} props.currentResult - The currently selected result.
 * @param {(value: string) => void} props.onDateChange - Handler for when the drawing date is changed.
 * @param {boolean} [props.enableLiveDrawView] - When true, show draw status instead of numbers when results are not yet available.
 * @param {boolean} [props.isLiveDrawView] - When true, shows the live draw panel in the header instead of the numbers or draw status.
 * @param {string} [props.className] - Optional additional class names for styling.
 * @returns {JSX.Element} The rendered ResultsHeader component.
 */
function ResultsHeader({ lotteryName, drawingDates, currentResult, onDateChange, enableLiveDrawView = false, isLiveDrawView = false, className, ...props }: ResultsHeaderProps) {
  const t = useTranslations('lotteries.dates');
  const liveDraw = useOptionalLiveDraw();
  const { drawingDate, numbers, extraNumbers } = currentResult;

  const classes = {
    container: twMerge('ll-grid-container h-auto py-8 px-4 xl:py-10 xl:px-0', className),
    select: `border-current/40 focus-visible:border-current data-[state=open]:border-current`,
    layout: 'll-container-inner flex flex-col gap-y-4',
  };

  const result = { numbers, extraNumbers };
  const lotteryResult = <LotteryResult result={result} lotteryName={lotteryName} className="light" />;
  const resultContent = enableLiveDrawView ? (
    <ResultsStatusOrNumbers lotteryName={lotteryName} lotteryResult={lotteryResult} drawingDate={drawingDate} hasResult={result.numbers.length > 0} />
  ) : (
    lotteryResult
  );
  const activeContent = isLiveDrawView ? <LiveDrawPanel /> : resultContent;
  const hasSelectedOption = drawingDates.some(option => option.value === drawingDate);
  const dateKey = ['HIGH_FREQUENCY', 'GO'].includes(getLotteryDrawFrequency(lotteryName)) ? 'results_dropdown_date_with_time' : 'results_dropdown_date';
  const dateOptions = hasSelectedOption ? drawingDates : [...drawingDates, { value: drawingDate, text: t(dateKey, { date: new Date(drawingDate) }) }];

  // A pick is the only thing that moves the live-draw board off the live sequence, so it is passed
  // on as an event rather than left for the board to infer from the selection changing.
  const handleDateChange = (value: string) => {
    onDateChange(value);
    liveDraw?.selectDrawing(value);
  };

  return (
    <LotteryHeaderContainer {...props} lotteryName={lotteryName} className={classes.container}>
      <LotteryLayout lotteryName={lotteryName} activeContent={activeContent} className={classes.layout} isHeader isLiveDrawView={isLiveDrawView} showArchivePanel>
        <Select id="results-date-selector" label="" options={dateOptions} value={drawingDate} onChange={handleDateChange} className={classes.select} data-track-name="Draw Date" />
      </LotteryLayout>
    </LotteryHeaderContainer>
  );
}

export { ResultsHeader };
export type { ResultsHeaderProps };
